import React from "react";
import { useDisclosure, useOs, useWindowEvent } from "@mantine/hooks";
import { useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useUserStatus } from "../../../../../../Utils/Hooks/useUserStatus";
import { useSharingHandlers, useWebPlayLoginState } from "../../../../../../Stores";
import { useInstallPrompt } from "../../../../Hooks/useInstallPrompt";
import {
	USER_STATUSES_WITH_LOGIN_ENABLED,
	USER_STATUSES_WITH_LOGOUT_ENABLED,
	capitalize
} from "../../../../../../Utils/Functions";
import { animated, useSpring } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import {
	Box,
	Button,
	CloseButton,
	Divider,
	Group,
	GroupedTransition,
	NavLink,
	Overlay,
	Paper,
	PaperProps,
	Portal,
	Space,
	Sx,
	Text,
	UnstyledButton
} from "@mantine/core";
import { Install, MenuBurger, MenuDrag, Share } from "../../../../../../Icons";
import { MdIosShare, MdLanguage, MdOutlineShoppingBag } from "react-icons/md";
import { openLoginWithFirebaseModal } from "../../../../../../Components/Modals";
import * as Analytics from "../../../../../../Utils/Analytics";
import {
	FxIdDomainSettingsUrlOpenMethod,
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform
} from "../../../../../../Api/gen";
import { IframeTypeToSupportedPlatformMap } from "../../../../Hooks";
import { HelmetWithChildren } from "../../../../../../Components/HelmetWithChildren";
import { UserIdBlock } from "./UserIdBlock";
import { getGameUrl, handleContactsClick, handleTosPpDocsLinkClick, openLogoutModal } from "./helpers";
import { IFxIdSdkAdapterChangeLanguageRequest } from "../../../../../../SDK/FxIdSdkBaseAdapter";
import { getDocsAndLegal, getValueFromConfigWithOverrides } from "../../../../../../Config";
import { defaultLocale } from "@app/i18n";
import { webPlayerStore } from "@app/Screens/WebPlayer/store/webPlayerStore";

type ChangeLanguageMenuProps = {
	languages: string[];
	onLanguageSelect?: () => void;
};

const ChangeLanguageMenuMobile = (props: ChangeLanguageMenuProps) => {
	const { i18n, t } = useTranslation();
	const { languages, onLanguageSelect } = props;
	return (
		<MobileMenuQuickAction
			title={t("change_language")}
			icon={<MdLanguage size={24} />}
			sx={{ position: "relative" }}
		>
			<select
				style={{ position: "absolute", inset: 0, opacity: 0 }}
				value={i18n.resolvedLanguage}
				onChange={(e) => {
					onLanguageSelect?.();
					void window.FxIdSdk?.ChangeLanguage(
						JSON.stringify({ lang: e.target.value } as IFxIdSdkAdapterChangeLanguageRequest)
					);
					void i18n.changeLanguage(e.target.value);
				}}
			>
				{languages.map((l) => (
					<option key={l} value={l}>
						{capitalize(new Intl.DisplayNames(l, { type: "language" }).of(l))}
					</option>
				))}
			</select>
		</MobileMenuQuickAction>
	);
};

//#region Mobile Menu
const MobileMenuQuickAction = (props: {
	title: string;
	icon: JSX.Element;
	onClick?: () => void;
	children?: React.ReactNode;
	sx?: Sx;
}) => {
	const { title, icon, onClick, children, sx } = props;
	return (
		<Box
			sx={(theme) => ({
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				gap: theme.spacing.xs,
				flexBasis: 0,
				flexGrow: 1,
				...sx
			})}
		>
			<UnstyledButton
				sx={(theme) => ({
					backgroundColor: theme.colors.gray[1],
					borderRadius: theme.radius.md,
					padding: theme.spacing.md
				})}
				onClick={onClick}
			>
				{icon}
			</UnstyledButton>
			<Text size="xs" lh={1.2} ta="center" sx={{ maxWidth: "min-content" }}>
				{title}
			</Text>
			{children}
		</Box>
	);
};

const IosHomeIcon = () => (
	<Text component="span" sx={{ display: "inline-block" }}>
		<MdIosShare size={18} style={{ marginTop: -4 }} />
	</Text>
);

const IosInstallMessageText = (props: { children?: React.ReactNode }) => <Text lh={1.2}>{props.children}</Text>;

const IosInstallMessage = (props: { open: boolean; onCloseButtonClick: () => void }) => {
	const { t } = useTranslation();
	const { onCloseButtonClick, open } = props;

	return (
		<>
			<HelmetWithChildren>
				<link
					rel="prefetch"
					as="video"
					href={`${import.meta.env.BASE_URL}/videos/ios-install.mp4`}
					type="video/mp4"
				/>
			</HelmetWithChildren>
			{open && (
				<Box sx={{ position: "absolute", inset: 0 }}>
					<Box
						w="100%"
						h="100%"
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "flex-end"
						}}
						p="lg"
					>
						<Paper
							bg="dark.6"
							sx={(theme) => ({ color: theme.white, position: "relative", zIndex: 999 })}
							radius="md"
							p="sm"
						>
							<Group spacing="sm" sx={{ flexWrap: "nowrap" }}>
								<Paper
									h={86}
									w={86}
									radius="md"
									component="video"
									src={`${import.meta.env.BASE_URL}/videos/ios-install.mp4`}
									autoPlay
									disablePictureInPicture
									playsInline
									loop
								/>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										justifyContent: "center"
									}}
								>
									<Trans
										t={t}
										i18nKey="game_screen.ios_install_message"
										components={[<IosHomeIcon key="0" />]}
										parent={IosInstallMessageText}
									/>
								</Box>
								<CloseButton
									onClick={onCloseButtonClick}
									variant="filled"
									radius="xl"
									sx={{ alignSelf: "flex-start" }}
								/>
							</Group>
						</Paper>
					</Box>
				</Box>
			)}
		</>
	);
};
//#endregion

const MobileQuickActions = (props: {
	showIosInstallMessage: () => void;
	onItemClick: () => void;
	showInstallPrompt: () => void;
	canInstall: boolean;
}) => {
	const { t } = useTranslation();
	const { onItemClick, showIosInstallMessage, canInstall, showInstallPrompt } = props;
	const portalConfig = webPlayerStore((state) => state.portalConfig);
	const adapter = webPlayerStore((state) => state.adapter);
	const gameConfig = webPlayerStore((state) => state.gameConfig);

	const os = useOs();
	const isIosStandalone =
		"standalone" in navigator && Boolean((navigator as unknown as Record<string, unknown>).standalone);
	const installAvailable = canInstall || (os === "ios" && !isIosStandalone);
	const onFxId = adapter === "fxid" || adapter === undefined;
	const showActions = installAvailable || "share" in navigator || (gameConfig && onFxId);
	const { sharingHandlers, linkCopyEnabled } = useSharingHandlers();
	const isAnySharingAllowed = [...sharingHandlers.values()].some((h) => h !== undefined) || linkCopyEnabled;

	// if (!isAnySharingAllowed) return null;

	if (!showActions) return null;
	return (
		<Group
			spacing="lg"
			sx={{ justifyContent: "space-evenly", alignItems: "flex-start", flexWrap: "nowrap" }}
			my="sm"
		>
			{installAvailable && (
				<MobileMenuQuickAction
					title={t("web_player.menu.install")}
					icon={<Install width={24} height={24} />}
					onClick={() => {
						onItemClick();
						if (os === "ios") {
							showIosInstallMessage();
						} else {
							void showInstallPrompt();
						}
					}}
				/>
			)}
			{isAnySharingAllowed && "share" in navigator && (
				<MobileMenuQuickAction
					title={t("web_player.menu.share")}
					icon={<Share width={24} height={24} />}
					onClick={() => {
						void navigator.share({ url: getGameUrl().toString() });
						void Analytics.site.share({
							game: gameConfig.GameSystemName,
							sdk_adapter: adapter
								? FxIdDomainStoreEnumsSupportedWebPublishingPlatform[
										IframeTypeToSupportedPlatformMap[adapter]
									]
								: undefined,
							method: "mobile_native_share"
						});
						onItemClick();
					}}
				/>
			)}
			{gameConfig && onFxId && (
				<ChangeLanguageMenuMobile languages={gameConfig.SupportedLanguages} onLanguageSelect={onItemClick} />
			)}
		</Group>
	);
};

const AnimatedPaper = animated(Paper as (props: PaperProps) => JSX.Element);

const MOBILE_MENU_DRAG_THRESHOLD = 10;
const MOBILE_MENU_MAX_OFFSET = MOBILE_MENU_DRAG_THRESHOLD * 1.5;

export const MenuMobile = () => {
	const { t, i18n } = useTranslation();

	const [opened, setOpened] = useState(false);
	const [iosInstallMessageOpen, { open: openIosInstallMessage, close: closeIosInstallMessage }] =
		useDisclosure(false);

	const userStatus = useUserStatus();
	const isLoginEnabled = useWebPlayLoginState();

	const gameConfig = webPlayerStore((state) => state.gameConfig);
	const portalConfig = webPlayerStore((state) => state.portalConfig);
	const adapter = webPlayerStore((state) => state.adapter);

	const { canInstall, showPrompt } = useInstallPrompt(gameConfig.GameSystemName);

	const docsFromPublicConfigAndBranding = getDocsAndLegal(portalConfig, gameConfig, adapter);
	const contactsUrl = getValueFromConfigWithOverrides<string>(
		portalConfig,
		"ContactsUrl",
		"ContactsUrl",
		adapter,
		gameConfig
	);

	const [{ y }, api] = useSpring(() => ({ y: MOBILE_MENU_MAX_OFFSET }));
	const bind = useDrag(
		({ active, movement: [, my], cancel }) => {
			if (my < MOBILE_MENU_DRAG_THRESHOLD * -1) cancel();
			if (my > MOBILE_MENU_DRAG_THRESHOLD) {
				setOpened(false);
				cancel();
			}
			api.start({ y: active ? my + MOBILE_MENU_MAX_OFFSET : MOBILE_MENU_MAX_OFFSET, immediate: active });
		},
		{
			bounds: { top: MOBILE_MENU_DRAG_THRESHOLD * -0.5, left: 0, right: 0, bottom: MOBILE_MENU_DRAG_THRESHOLD },
			rubberband: 0.8
		}
	);
	const showLoginButton = isLoginEnabled && USER_STATUSES_WITH_LOGIN_ENABLED.includes(userStatus);
	const onFxId = adapter === "fxid" || adapter === undefined;
	const showLogoutButton = onFxId && USER_STATUSES_WITH_LOGOUT_ENABLED.includes(userStatus);

	const overlayRef = useRef<HTMLDivElement>(null);
	const clickTarget = useRef<EventTarget | null>(null);

	// Если инфо для игры нет или специально указано что там undefined - то не показываем строчку
	let isContactsEnabled = false;
	if (onFxId) {
		const legalContacts = docsFromPublicConfigAndBranding.legalContacts
			? docsFromPublicConfigAndBranding.legalContacts
			: gameConfig.PublicWebClientConfig.LegalContacts;
		if (legalContacts != null) {
			const locale = i18n.resolvedLanguage ?? defaultLocale;

			// Если язык но он  undefined или нулл то ничего не показываем. Поидее это должно выше проверятся и сюда прийти не должны
			if (Object.prototype.hasOwnProperty.call(legalContacts, locale)) {
				const localeLegalInfo = legalContacts[locale];

				if (localeLegalInfo != null) {
					isContactsEnabled = true;
				}
			} else if (legalContacts.default != null) {
				isContactsEnabled = true;
			}
		}
	} else {
		if (contactsUrl != null) {
			isContactsEnabled = true;
		}
	}

	useWindowEvent("mousedown", (e) => {
		clickTarget.current = e.target;
	});

	useWindowEvent("touchstart", (e) => {
		clickTarget.current = e.target;
	});

	useWindowEvent("blur", () => setOpened(false));

	const handleOutsideClick = () => {
		if (clickTarget.current === overlayRef.current) {
			setOpened(false);
		}
	};

	return (
		<>
			<div className="header-menu-icon">
				<UnstyledButton onClick={() => setOpened(true)}>
					<MenuBurger fill="#fff" width={16} height={16} />
				</UnstyledButton>
			</div>
			<Portal target="#web-player">
				<GroupedTransition
					mounted={opened}
					transitions={{
						overlay: { duration: 125, transition: "fade", timingFunction: "ease" },
						menu: { duration: 250, transition: "slide-up" }
					}}
				>
					{(transitionStyles) => (
						<Box sx={{ position: "fixed", inset: 0, zIndex: 1000 }}>
							<div style={transitionStyles.overlay}>
								<Overlay color="#000" zIndex={0} />
							</div>
							<Box
								ref={overlayRef}
								sx={{
									position: "absolute",
									inset: 0,
									display: "flex",
									justifyContent: "center",
									alignItems: "flex-end"
								}}
								style={transitionStyles.menu}
								onClick={handleOutsideClick}
							>
								<AnimatedPaper
									className="header-menu-container"
									radius={0}
									sx={(theme) => ({
										borderRadius: `${theme.radius.lg}px ${theme.radius.lg}px 0 0`,
										width: 300,
										maxHeight: "98vh",
										display: "flex",
										flexDirection: "column"
									})}
									style={{ y }}
								>
									<Group
										className="header-menu-drag"
										{...bind()}
										sx={{ height: 32, touchAction: "none" }}
										align="center"
										position="center"
									>
										<MenuDrag />
									</Group>
									<Box sx={{ overflowY: "auto" }}>
										{showLoginButton && (
											<Box px="sm">
												<Button
													compact
													sx={{ height: 32 }}
													px={10}
													size="xs"
													fullWidth
													rightIcon={<MdOutlineShoppingBag size={16} />}
													onClick={() => {
														setOpened(false);
														openLoginWithFirebaseModal({
															target: "#web-player",
															authProviders: {
																allowed:
																	gameConfig?.PublicWebClientConfig.Auth
																		?.AllowedAuthProviders ??
																	portalConfig.PortalPublicWebConfig.CompanyBranding
																		.Auth.AllowedAuthProviders
															},
															gameConfig: gameConfig,
															portalConfig: portalConfig,
															adapter: adapter,
															fullScreen: true,
															onAuthSuccessful: () => window.location.reload()
														});
													}}
												>
													{t("sign_in_modal.title")}
												</Button>
											</Box>
										)}
										<MobileQuickActions
											onItemClick={() => setOpened(false)}
											showIosInstallMessage={openIosInstallMessage}
											canInstall={canInstall}
											showInstallPrompt={showPrompt}
										/>
										{/* TODO: Вернуть, когда игра начнет поддерживать репорт проблемы */}
										{/* <Divider my="sm" />
										<NavLink
											label={t("web_player.menu.report_issue")}
											icon={<Email width={24} height={24} />}
											onClick={() => {
												setOpened(false);
												void window.FxIdSdk?.CreateSupportRequest();
											}}
										/> */}
										{showLogoutButton && (
											<>
												<Divider mt="sm" />
												<NavLink
													py="xs"
													onClick={() => {
														setOpened(false);
														openLogoutModal(t, <Text>{t("logout_modal.text")}</Text>);
													}}
													label={<Text color="dimmed">{t("user_settings_menu.logout")}</Text>}
												/>
											</>
										)}
										<Divider mb="sm" />
										{docsFromPublicConfigAndBranding.pp && (
											<NavLink
												label={t("pp")}
												onClick={() => {
													setOpened(false);
													handleTosPpDocsLinkClick(
														String(docsFromPublicConfigAndBranding.pp?.url) || "",
														docsFromPublicConfigAndBranding.pp?.openMethod ||
															FxIdDomainSettingsUrlOpenMethod.Unspecified
													);
												}}
											/>
										)}
										{docsFromPublicConfigAndBranding.tos && (
											<NavLink
												label={t("tos")}
												onClick={() => {
													setOpened(false);
													handleTosPpDocsLinkClick(
														String(docsFromPublicConfigAndBranding.tos?.url) || "",
														docsFromPublicConfigAndBranding.tos?.openMethod ||
															FxIdDomainSettingsUrlOpenMethod.Unspecified
													);
												}}
											/>
										)}
										<Divider mb="sm" />
										{isContactsEnabled && (
											<>
												<NavLink
													onClick={() => {
														setOpened(false);
														handleContactsClick(
															gameConfig.GameSystemName,
															adapter,
															contactsUrl
														);
													}}
													label={t("legal_contacts")}
												/>
												<Divider mb="sm" />
											</>
										)}
										<UserIdBlock />
									</Box>
									<Space h={MOBILE_MENU_MAX_OFFSET} />
								</AnimatedPaper>
							</Box>
						</Box>
					)}
				</GroupedTransition>
				<IosInstallMessage open={iosInstallMessageOpen} onCloseButtonClick={closeIosInstallMessage} />
			</Portal>
		</>
	);
};
